import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'

import IconCaretDown from '@vtex/styleguide/lib/icon/CaretDown'
import IconCaretUp from '@vtex/styleguide/lib/icon/CaretUp'
import { calculateAttributeType } from '@/checkout-instore-legacy/components/FilterSidebar/hook'
import {
  Range,
  CheckboxList,
} from '@/checkout-instore-legacy/components/FilterSidebar/FilterContent/FiltersType'
import { FilterAttribute } from '@/checkout-instore-legacy/components/FilterSidebar/typings'
import { DatePickerRange } from './FiltersType/DatePickerRange'
import { getIntlMessage } from '@/checkout-instore-legacy/i18n/utils'

type FilterContentProps = {
  attributes: FilterAttribute[]
  minimumCollapsibleItemsToDisplay?: number
  handleChangeFilter: (
    key: string
  ) => (attributeKey: string, attributeValue: any) => void
}

export const FilterContent = ({
  attributes,
  handleChangeFilter,
  minimumCollapsibleItemsToDisplay,
}: FilterContentProps) => {
  return (
    <div data-testid="search-filters-content" className="flex flex-column">
      <div className="w-100">
        {attributes &&
          attributes.map((attribute, index) => (
            <FilterAttributesUnit
              attribute={attribute}
              key={`${attribute.key}-${index}`}
              onChange={handleChangeFilter(attribute.key)}
              minimumCollapsibleItemsToDisplay={
                minimumCollapsibleItemsToDisplay
              }
            />
          ))}
      </div>
    </div>
  )
}

type FilterAttributesUnitProps = {
  attribute: FilterAttribute
  onChange: (key: string, value: any) => void
  minimumCollapsibleItemsToDisplay?: number
}

const FilterAttributesUnit = ({
  attribute,
  onChange,
  minimumCollapsibleItemsToDisplay,
}: FilterAttributesUnitProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [valuesDisplay, setValuesDisplay] = useState<FilterAttribute['values']>(
    []
  )
  const { values } = attribute
  let { label } = attribute

  if (!label) {
    label = getIntlMessage(`filters.label.${attribute.key}`) || ''
  }

  const MIN_FILTERS_COLLAPSIBLE_DISPLAY = minimumCollapsibleItemsToDisplay || 2
  const MAX_FILTERS_WITHOUT_COLLAPSIBLE = MIN_FILTERS_COLLAPSIBLE_DISPLAY + 1
  const COLLAPSE_ICON_CARET_SIZE = 10

  useEffect(() => {
    collapseFilterValues(isCollapsed)
    // no need to pass collapseFilterValues and isCollapsed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attribute])

  if (!values) {
    return null
  }

  const handleCollpaseFilter = () => {
    const toggleCollapsed = !isCollapsed
    setIsCollapsed(toggleCollapsed)
    collapseFilterValues(toggleCollapsed)
  }

  const collapseFilterValues = (collapsed: boolean) => {
    if (isFiltersCollapsable && collapsed) {
      setValuesDisplay([...activeValues, ...inactiveCollapseValues])
    } else {
      setValuesDisplay(attribute.values || [])
    }
  }

  const activeValues = values.filter((value) => value.active)
  const inactiveValues = values.filter((value) => !value.active)
  const inactiveCollapseValues = inactiveValues.slice(
    0,
    MIN_FILTERS_COLLAPSIBLE_DISPLAY
  )

  const isFiltersCollapsable =
    inactiveValues.length > MAX_FILTERS_WITHOUT_COLLAPSIBLE

  const [IconCaretComponent, idCollapseMessage] = isCollapsed
    ? [IconCaretDown, 'filterMoreOptions']
    : [IconCaretUp, 'filterLessOptions']

  return (
    <div className="mt6 flex justify-content flex-column">
      <span className="t-mini mid-gray fw4 nb2">{label}</span>
      <div>
        <Attribute
          attribute={attribute}
          values={valuesDisplay}
          onChange={onChange}
        />
        {isFiltersCollapsable ? (
          <div
            className="f7 fw5 c-link hover-c-link active-c-link visited-c-link pointer mt4 pt1"
            onClick={handleCollpaseFilter}
            data-testid={`${label}-more-options`}
          >
            <span className="pr3">
              <IconCaretComponent
                size={COLLAPSE_ICON_CARET_SIZE}
                color="currentColor"
              />
            </span>
            <FormattedMessage id={idCollapseMessage} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

type AttributeProps = {
  attribute: FilterAttribute
  values: FilterAttribute['values']
  onChange: (attributeKey: string, attributeValue: any) => void
}

const Attribute = ({ attribute, values, onChange }: AttributeProps) => {
  const type = calculateAttributeType(attribute)

  switch (type) {
    case 'checkbox': {
      return (
        <div className="mt5 f6">
          <CheckboxList
            attribute={attribute}
            values={values}
            onChange={onChange}
          />
        </div>
      )
    }
    case 'range': {
      const rangeType = attribute.key === 'price' ? 'price' : 'default'
      return (
        <div className="mt5 f6 pl3">
          <Range attribute={attribute} onChange={onChange} type={rangeType} />
        </div>
      )
    }

    case 'daterange': {
      return (
        <div className="mt5 f6 pl3 w-100">
          <DatePickerRange
            attribute={attribute}
            onChange={onChange}
            values={values}
          />
        </div>
      )
    }

    default:
      return null
  }
}
